import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Sparkles, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";

import { useTranslation } from "react-i18next";
/**
 * TrialBanner — shows the dealer their subscription/trial status.
 *
 * The Upgrade CTA routes to `/pricing` (in-app only shown to logged-in dealers
 * within 30 days of trial expiry per Phase D+E product decisions).
 */
export default function TrialBanner({ pump, onUpgrade, onDismiss, compact = false }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const view = useMemo(() => computeView(pump), [pump]);
  if (!view) return null;

  const handleCta = () => {
    if (onUpgrade) onUpgrade();
    nav("/pricing");
  };

  const {
    tone,           // emerald | amber | red
    icon: Icon,
    title,
    body,
    ctaLabel,
    dismissible,
  } = view;

  const tones = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100",
    amber: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100",
    red: "border-red-300 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100",
  };
  const iconTones = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    red: "text-red-600 dark:text-red-400",
  };
  const btnTones = {
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    amber: "bg-amber-600 hover:bg-amber-700",
    red: "bg-red-600 hover:bg-red-700",
  };

  // Brick 5 N3 — for celebratory / non-urgent trials (>30d remaining, emerald tone)
  // we surface a small chip in the sidebar/header instead of dominating the
  // primary viewport. Callers pass compact=true to opt in. Urgent tones
  // (amber ≤30d, red ≤7d / expired) still render as the full banner.
  if (compact && tone === "emerald") {
    return (
      <div
        className={"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold " + tones[tone]}
        data-testid="trial-banner-compact"
        title={body}
      >
        <Icon className={"h-3 w-3 " + iconTones[tone]} />
        <span>{title}</span>
      </div>
    );
  }

  return (
    <div
      className={"flex items-start gap-3 rounded-xl border-2 p-3.5 sm:p-4 " + tones[tone]}
      data-testid="trial-banner"
    >
      <div className={"mt-0.5 shrink-0 " + iconTones[tone]}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold leading-tight sm:text-base">{title}</div>
        <div className="mt-1 text-[12px] leading-snug opacity-90 sm:text-xs">{body}</div>
        {ctaLabel && (
          <Button
            size="sm"
            onClick={handleCta}
            className={"mt-2.5 h-9 text-white " + btnTones[tone]}
            data-testid="trial-cta"
          >
            {ctaLabel}
          </Button>
        )}
      </div>
      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={"ml-1 shrink-0 rounded-md p-1.5 opacity-60 transition hover:opacity-100 " + iconTones[tone]}
          aria-label={t("components.trialbanner.dismiss")}
          data-testid="trial-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function computeView(pump) {
  if (!pump) return null;
  const status = pump.subscription_status || "TRIAL";
  if (status === "ACTIVE") return null; // paid dealer — no banner

  const expiresAt = pump.trial_expires_at;
  if (!expiresAt) return null;
  const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000);

  if (status === "EXPIRED" || days < 0) {
    return {
      tone: "red",
      icon: AlertTriangle,
      title: "Trial ended",
      body: "Your 6-month free trial is over. Upgrade to keep using HISAB verify, cashbook, reports & CA add-on. All your data is safe and ready when you upgrade.",
      ctaLabel: "Upgrade now",
      dismissible: false,
    };
  }
  if (days <= 7) {
    return {
      tone: "red",
      icon: AlertTriangle,
      title: `Trial ends in ${days} day${days === 1 ? "" : "s"}`,
      body: "Upgrade now to avoid any interruption. Your data, staff logins & CA link stay exactly as they are.",
      ctaLabel: "Upgrade now",
      dismissible: true,
    };
  }
  if (days <= 30) {
    return {
      tone: "amber",
      icon: Clock,
      title: `Trial ends in ${days} days`,
      body: "You're loving HisabX so far — lock in your subscription now for uninterrupted access.",
      ctaLabel: "See plans",
      dismissible: true,
    };
  }
  // > 30 days remaining — celebratory
  return {
    tone: "emerald",
    icon: Sparkles,
    title: "You're on your 6-month free trial",
    body: `${days} days remaining · Explore every feature, invite your team & CA — no card required.`,
    ctaLabel: null,     // don't push CTA when they've just started
    dismissible: true,
  };
}
